(() => {
  const ROOT_SELECTOR = '[data-sj-cinematic]';
  const WORLD_NAMES = [
    'STREETJAM',
    'BLINKIE',
    'KOMIKS',
    'KURBEE',
    'ZOLTRA',
    'ZEBRA HEART',
    'BEAM BLITZ',
    'WHEELERS',
    'MAGIC UNICORN',
    'KITTY CAT',
  ];
  const WORLD_TRANSPORT = [
    'ENTER STREETJAM',
    'VIEW BLINKIE',
    'ROLL WITH KOMIKS',
    'CHASE WITH KURBEE',
    'LIGHT UP WITH ZOLTRA',
    'MEET ZEBRA HEART',
    'SWITCH ON BEAM BLITZ',
    'RUN WITH WHEELERS',
    'MAKE MAGIC LOUD',
    'LAND WITH KITTY CAT',
  ];
  const JAMVERSE_HOLD_FRACTION = 0.68;

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smooth = (value) => value * value * (3 - 2 * value);
  const easeOut = (value) => 1 - Math.pow(1 - value, 3);
  const segment = (value, start, end) => smooth(clamp((value - start) / Math.max(end - start, 0.0001)));

  const mobileProductSource = (source) => {
    if (!source) return source;
    let mobileSource = source
      .replace('-cutout-1500.png', '-cutout-720.png')
      .replace('-1200.png', '-720.png')
      .replace('-v5-web.png', '-v5-mobile.png');
    mobileSource = mobileSource.replace(
      /([?&]width=)\d+/,
      (_match, prefix) => `${prefix}720`,
    );
    if (
      mobileSource.includes('cdn.shopify.com')
      && !/[?&]width=\d+/.test(mobileSource)
    ) {
      mobileSource += `${mobileSource.includes('?') ? '&' : '?'}width=720`;
    }
    return mobileSource;
  };

  const stageJamverseProgress = (progress, worldCount) => {
    const finalIndex = Math.max(worldCount - 1, 1);
    const rawScaled = clamp(progress) * finalIndex;
    if (rawScaled >= finalIndex) return { progress: 1, scaled: finalIndex };

    const index = Math.floor(rawScaled);
    const phase = rawScaled - index;
    const transition = smooth(clamp(
      (phase - JAMVERSE_HOLD_FRACTION) / Math.max(1 - JAMVERSE_HOLD_FRACTION, 0.0001),
    ));
    const scaled = index + transition;
    return { progress: scaled / finalIndex, scaled };
  };

  const runwayProgress = (element) => {
    if (!element) return 0;
    const rect = element.getBoundingClientRect();
    const travel = Math.max(element.offsetHeight - window.innerHeight, 1);
    return clamp(-rect.top / travel);
  };

  const viewportProgress = (element) => {
    if (!element) return 0;
    const rect = element.getBoundingClientRect();
    return clamp((window.innerHeight - rect.top) / (window.innerHeight + rect.height));
  };

  const setPercent = (element, progress, suffix = '') => {
    if (!element) return;
    element.textContent = `${String(Math.round(progress * 100)).padStart(2, '0')}${suffix}`;
  };

  const initialiseColorSelectors = (root) => {
    root.querySelectorAll('[data-sj-color-selector]').forEach((selector) => {
      if (selector.dataset.sjColorInitialised === 'true') return;
      selector.dataset.sjColorInitialised = 'true';

      const world = selector.closest('[data-sj-color-world]');
      const image = world?.querySelector('[data-sj-color-image]');
      const label = selector.querySelector('[data-sj-color-name]');
      const choices = [...selector.querySelectorAll('[data-sj-color-choice]')];
      if (!world || !image || choices.length === 0) return;

      const preloadChoice = (choice) => {
        const source = choice.dataset.sjSrc;
        if (!source || choice.dataset.sjPreloaded === 'true') return;
        const preload = new Image();
        preload.decoding = 'async';
        preload.src = window.innerWidth < 750 ? mobileProductSource(source) : source;
        choice.dataset.sjPreloaded = 'true';
      };

      const applyChoice = (choice) => {
        const source = choice.dataset.sjSrc;
        if (!source) return;
        const mobileSource = mobileProductSource(source);
        const renderedSource = window.innerWidth < 750 ? mobileSource : source;
        const sourceWidthFromName = source.match(/-(\d+)\.png(?:[?#]|$)/)?.[1];
        const sourceWidthFromQuery = new URL(source, window.location.href).searchParams.get('width');
        const sourceWidth = Number(sourceWidthFromName || sourceWidthFromQuery) || 1500;
        const responsiveSourceSet = mobileSource && mobileSource !== source
          ? `${mobileSource} 720w, ${source} ${sourceWidth}w`
          : source;
        const responsiveSizes = image.dataset.sjOriginalSizes
          || image.getAttribute('sizes')
          || '(max-width: 749px) 240px, 54vw';
        const sequence = Number(image.dataset.sjColorSequence || 0) + 1;

        choices.forEach((item) => item.setAttribute('aria-pressed', String(item === choice)));
        if (label) label.textContent = choice.dataset.sjLabel || 'SELECTED';
        world.dataset.sjColor = choice.dataset.sjLabel || '';
        world.style.setProperty('--sj-color-a', choice.dataset.sjColorA || '#47e7ff');
        world.style.setProperty('--sj-color-b', choice.dataset.sjColorB || '#164cff');
        world.style.setProperty('--sj-color-ink', choice.dataset.sjColorInk || '#071a4a');

        image.dataset.sjFullProductSrc = source;
        image.dataset.sjMobileProductSrc = mobileSource;
        image.dataset.sjOriginalSrc = source;
        image.dataset.sjMobileSrc = mobileSource;
        image.dataset.sjOriginalSrcset = responsiveSourceSet;
        image.dataset.sjOriginalSizes = responsiveSizes;
        image.dataset.sjColorSequence = String(sequence);

        /*
         * A responsive image keeps using its selected srcset candidate even
         * after img.src changes. Replace every candidate atomically so rapid
         * colour changes cannot leave the previous scooter painted.
         */
        image.closest('picture')?.querySelectorAll('source').forEach((responsiveSource) => {
          responsiveSource.srcset = responsiveSourceSet;
          responsiveSource.sizes = responsiveSizes;
        });
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = renderedSource;
        if (window.innerWidth >= 750) {
          image.srcset = responsiveSourceSet;
          image.sizes = responsiveSizes;
        }
        image.alt = choice.dataset.sjAlt || image.alt;
        image.classList.add('is-switching');

        const settle = () => {
          if (image.dataset.sjColorSequence === String(sequence)) {
            image.classList.remove('is-switching');
          }
        };
        if (image.decode) image.decode().then(settle).catch(settle);
        else image.addEventListener('load', settle, { once: true });
      };

      choices.forEach((choice) => {
        choice.addEventListener('click', () => applyChoice(choice));
        choice.addEventListener('pointerenter', () => preloadChoice(choice), { passive: true });
        choice.addEventListener('focus', () => preloadChoice(choice), { passive: true });
      });
    });
  };

  const initialiseFlagshipColorSelectors = (root) => {
    if (root.dataset.sjFlagshipColourways !== 'ready') return;

    const configNode = root.querySelector('[data-sj-flagship-colour-config]');
    if (!configNode) return;

    let configurations = [];
    try {
      configurations = JSON.parse(configNode.textContent || '[]');
    } catch (error) {
      console.error('StreetJam flagship colour configuration is invalid.', error);
      return;
    }

    configurations.forEach((configuration) => {
      const world = root.querySelector(`[data-sj-world="${configuration.world}"]`);
      const image = world?.querySelector('.sj-world__product--helmet > img');
      const choices = Array.isArray(configuration.choices) ? configuration.choices : [];
      if (!world || !image || choices.length < 2) return;

      world.dataset.sjColorWorld = configuration.key;
      world.dataset.sjFlagshipColours = 'ready';
      image.setAttribute('data-sj-color-image', '');

      const selector = document.createElement('div');
      selector.className = 'sj-world__color-selector';
      selector.setAttribute('data-sj-color-selector', '');
      selector.setAttribute('aria-label', `Choose a ${configuration.label} colour`);

      const caption = document.createElement('span');
      caption.append('HELMET COLOUR ');
      const selectedLabel = document.createElement('b');
      selectedLabel.setAttribute('data-sj-color-name', '');
      selectedLabel.textContent = choices[0].label;
      caption.appendChild(selectedLabel);
      selector.appendChild(caption);

      const buttonGroup = document.createElement('div');
      choices.forEach((choice, choiceIndex) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('data-sj-color-choice', '');
        button.setAttribute('aria-pressed', String(choiceIndex === 0));
        button.setAttribute('aria-label', `Show ${choice.label} ${configuration.label}`);
        button.dataset.sjSrc = `../theme/assets/${choice.asset}`;
        button.dataset.sjLabel = choice.label;
        button.dataset.sjAlt = `${configuration.label} - ${choice.label}`;
        button.dataset.sjColorA = choice.a;
        button.dataset.sjColorB = choice.b;
        button.dataset.sjColorInk = choice.ink;

        const swatch = document.createElement('i');
        swatch.style.setProperty('--sj-swatch', choice.swatch);
        button.appendChild(swatch);
        buttonGroup.appendChild(button);
      });
      selector.appendChild(buttonGroup);
      world.appendChild(selector);
    });
  };

  const hydrateHelmetThumbImages = (scope, centerIndex = null, radius = Number.POSITIVE_INFINITY) => {
    scope?.querySelectorAll('[data-sj-helmet-thumb-img]').forEach((image) => {
      const source = image.dataset.sjHelmetThumbSrc;
      const indexedItem = image.closest('[data-sj-helmet-index]');
      const imageIndex = Number(indexedItem?.dataset.sjHelmetIndex);
      const insideWindow = centerIndex === null
        || !Number.isFinite(imageIndex)
        || Math.abs(imageIndex - centerIndex) <= radius;

      if (insideWindow && source && !image.getAttribute('src')) image.src = source;
    });
  };

  const initialise = (root) => {
    if (root.dataset.sjInitialised === 'true') return;
    root.classList.add('sj-js');
    initialiseFlagshipColorSelectors(root);
    initialiseColorSelectors(root);
    const qaMode = new URLSearchParams(window.location.search).get('sj-qa');
    if (qaMode === 'static') {
      root.dataset.sjInitialised = 'true';
      hydrateHelmetThumbImages(root);
      root.querySelectorAll('[data-sj-reveal]').forEach((item) => item.classList.add('is-visible'));
      return;
    }
    if (qaMode === 'motion-off') root.dataset.motion = 'off';
    root.dataset.sjInitialised = 'true';

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const motionOff = root.dataset.motion === 'off';

    const syncMobileDocumentState = () => {
      document.documentElement.classList.toggle('sj-mobile-jamverse', window.innerWidth < 750);
    };
    syncMobileDocumentState();

    if (reducedMotion || motionOff) {
      root.classList.add('sj-reduced-motion');
      hydrateHelmetThumbImages(root);
      root.querySelectorAll('[data-sj-reveal]').forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const nodes = {
      jamverse: root.querySelector('[data-sj-jamverse]'),
      jamverseSticky: root.querySelector('.sj-jamverse__sticky'),
      jamverseWorlds: root.querySelector('[data-sj-jamverse-worlds]'),
      jamverseChapters: root.querySelector('.sj-jamverse__chapters'),
      worlds: [...root.querySelectorAll('[data-sj-world]')],
      worldNumber: root.querySelector('[data-sj-world-number]'),
      worldName: root.querySelector('[data-sj-world-name]'),
      worldTransport: root.querySelector('[data-sj-world-transport]'),
      jamversePercent: root.querySelector('[data-sj-jamverse-percent]'),
      jamverseButtons: [...root.querySelectorAll('[data-sj-jamverse-jump]')],
      jamversePrev: root.querySelector('[data-sj-jamverse-prev]'),
      jamverseNext: root.querySelector('[data-sj-jamverse-next]'),
      pointerStage: root.querySelector('[data-sj-pointer-stage]'),
      mascots: [...root.querySelectorAll('[data-sj-mascot]')],
      cursorLight: root.querySelector('[data-sj-cursor-light]'),
      atmosphere: root.querySelector('[data-sj-atmosphere]'),
      brandStrike: root.querySelector('[data-sj-brand-strike]'),
      rail: root.querySelector('[data-sj-rail]'),
      railTrack: root.querySelector('.sj-cine-product-rail'),
      railPercent: root.querySelector('[data-sj-rail-percent]'),
      railCards: [...root.querySelectorAll('.sj-cine-rail-card')],
      garage: root.querySelector('[data-sj-garage]'),
      garageName: root.querySelector('[data-sj-garage-name]'),
      garageMarkers: [...root.querySelectorAll('[data-sj-garage-jump]')],
      garagePrev: root.querySelector('[data-sj-garage-prev]'),
      garageNext: root.querySelector('[data-sj-garage-next]'),
      helmetRiot: root.querySelector('[data-sj-helmet-riot]'),
      helmetCarousel: root.querySelector('[data-sj-helmet-carousel]'),
      helmetCards: [...root.querySelectorAll('[data-sj-helmet-card]')],
      helmetRear: root.querySelector('[data-sj-helmet-rear]'),
      helmetHeroWrap: root.querySelector('[data-sj-helmet-hero-wrap]'),
      helmetHero: root.querySelector('[data-sj-helmet-hero]'),
      helmetLinks: [...root.querySelectorAll('[data-sj-helmet-link]')],
      helmetTitle: root.querySelector('[data-sj-helmet-title]'),
      helmetPrice: root.querySelector('[data-sj-helmet-price]'),
      helmetStatus: root.querySelector('[data-sj-helmet-status]'),
      helmetIndex: root.querySelector('[data-sj-helmet-index]'),
      helmetMood: root.querySelector('[data-sj-helmet-mood]'),
      helmetMoodCopy: root.querySelector('[data-sj-helmet-mood-copy]'),
      helmetPercent: root.querySelector('[data-sj-helmet-percent]'),
      helmetMarkers: [...root.querySelectorAll('[data-sj-helmet-jump]')],
      helmetPrev: root.querySelector('[data-sj-helmet-prev]'),
      helmetNext: root.querySelector('[data-sj-helmet-next]'),
      helmetLive: root.querySelector('[data-sj-helmet-live]'),
      helmetPointer: root.querySelector('[data-sj-helmet-pointer]'),
      clarity: root.querySelector('[data-sj-clarity]'),
      clarityRows: [...root.querySelectorAll('.sj-cine-clarity__grid > a')],
      finale: root.querySelector('[data-sj-finale]'),
    };

    const isFullMotion = root.dataset.motion !== 'subtle';
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const canvasContext = nodes.atmosphere?.getContext('2d', { alpha: true }) || null;
    const particleCount = window.innerWidth < 750 ? 8 : isFullMotion ? 92 : 48;
    const particles = Array.from({ length: particleCount }, (_, index) => ({
      x: ((index * 0.61803398875) + 0.17) % 1,
      y: ((index * 0.38196601125) + 0.31) % 1,
      speed: 0.45 + ((index * 17) % 23) / 23,
      size: 0.45 + ((index * 11) % 19) / 19,
      phase: ((index * 29) % 101) / 101,
      kind: index % 5,
    }));

    let rafId = 0;
    let needsUpdate = true;
    let lastScrollY = window.scrollY;
    let scrollVelocity = 0;
    let pointerTargetX = 0;
    let pointerTargetY = 0;
    let pointerX = 0;
    let pointerY = 0;
    let mascotReyX = 0;
    let mascotReyY = 0;
    let mascotMiraX = 0;
    let mascotMiraY = 0;
    let mascotZippX = 0;
    let mascotZippY = 0;
    let cursorClientX = window.innerWidth * 0.5;
    let cursorClientY = window.innerHeight * 0.5;
    let pointerInJamverse = false;
    let jamverseVisible = true;
    let garageVisible = false;
    let helmetRiotVisible = false;
    let jamverseRawProgress = 0;
    let mobileGestureProgress = null;
    let mobileSelectedProgress = window.innerWidth < 750 ? 0 : null;
    let mobileSwipe = null;
    let suppressWorldClickUntil = 0;
    let activeWorld = 0;
    let activeGarage = 0;
    let activeHelmet = 0;
    let canvasWidth = 0;
    let canvasHeight = 0;
    let canvasDpr = 1;
    let lastAtmosphereDraw = 0;
    let previousFrameTime = performance.now();
    const loadedHelmetHeroes = new Set();

    const worldMedia = nodes.worlds.map((world) => [...world.querySelectorAll('img')]);
    const garageMedia = nodes.railCards.map((card) => [...card.querySelectorAll('img')]);
    const hydratedMobileGarageCards = new Set();

    const dehydrateWorldImage = (image) => {
      const source = image.getAttribute('src');
      const sourceSet = image.getAttribute('srcset');
      const sizes = image.getAttribute('sizes');
      const fullSource = image.dataset.sjFullProductSrc
        || image.dataset.sjOriginalSrc
        || source;
      if (fullSource) {
        image.dataset.sjOriginalSrc = fullSource;
        image.dataset.sjMobileSrc = image.dataset.sjMobileProductSrc
          || mobileProductSource(fullSource);
      }
      if (sourceSet && !image.dataset.sjOriginalSrcset) image.dataset.sjOriginalSrcset = sourceSet;
      if (sizes && !image.dataset.sjOriginalSizes) image.dataset.sjOriginalSizes = sizes;
      image.removeAttribute('src');
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
    };

    const hydrateWorldImage = (image) => {
      const phoneJourney = window.innerWidth < 750;
      const source = phoneJourney ? image.dataset.sjMobileSrc : image.dataset.sjOriginalSrc;
      if (source && image.getAttribute('src') !== source) image.src = source;
      if (!phoneJourney && image.dataset.sjOriginalSrcset) image.srcset = image.dataset.sjOriginalSrcset;
      if (!phoneJourney && image.dataset.sjOriginalSizes) image.sizes = image.dataset.sjOriginalSizes;
    };

    const updateMobileWorldMedia = (centerIndex) => {
      const phoneJourney = window.innerWidth < 750;
      worldMedia.forEach((images, worldIndex) => {
        const shouldHydrate = !phoneJourney || Math.abs(worldIndex - centerIndex) <= 1;
        images.forEach((image) => {
          if (shouldHydrate) hydrateWorldImage(image);
          else dehydrateWorldImage(image);
        });
      });
    };

    const updateMobileGarageMedia = (centerIndex, sectionVisible = garageVisible) => {
      const phoneLayout = window.innerWidth < 750;
      if (phoneLayout && sectionVisible) {
        hydratedMobileGarageCards.add(centerIndex);
        hydratedMobileGarageCards.add(Math.max(centerIndex - 1, 0));
        hydratedMobileGarageCards.add(Math.min(centerIndex + 1, garageMedia.length - 1));
      }
      garageMedia.forEach((images, cardIndex) => {
        const shouldHydrate = !phoneLayout
          || hydratedMobileGarageCards.has(cardIndex);
        images.forEach((image) => {
          if (shouldHydrate) hydrateWorldImage(image);
          else dehydrateWorldImage(image);
        });
      });
    };

    const buildHelmetRear = () => {
      if (!nodes.helmetRear || !nodes.helmetCards.length || nodes.helmetRear.childElementCount) return;
      const fragment = document.createDocumentFragment();
      nodes.helmetCards.forEach((card) => {
        const image = card.querySelector('img');
        if (!image) return;
        const item = document.createElement('span');
        item.className = 'sj-helmet-riot__ghost';
        item.dataset.sjHelmetIndex = card.dataset.sjHelmetIndex || '';
        const clone = image.cloneNode(false);
        clone.alt = '';
        clone.setAttribute('aria-hidden', 'true');
        clone.loading = 'lazy';
        item.appendChild(clone);
        fragment.appendChild(item);
      });
      nodes.helmetRear.appendChild(fragment);
    };

    if (window.innerWidth >= 990) buildHelmetRear();
    updateMobileWorldMedia(0);
    updateMobileGarageMedia(0, false);

    const resizeCanvas = () => {
      if (!nodes.atmosphere || !canvasContext) return;
      canvasDpr = window.innerWidth < 750 ? 1 : Math.min(window.devicePixelRatio || 1, 1.5);
      canvasWidth = Math.max(window.innerWidth, 1);
      canvasHeight = Math.max(window.innerHeight, 1);
      nodes.atmosphere.width = Math.round(canvasWidth * canvasDpr);
      nodes.atmosphere.height = Math.round(canvasHeight * canvasDpr);
      nodes.atmosphere.style.width = `${canvasWidth}px`;
      nodes.atmosphere.style.height = `${canvasHeight}px`;
      canvasContext.setTransform(canvasDpr, 0, 0, canvasDpr, 0, 0);
    };

    const wrap = (value, maximum) => ((value % maximum) + maximum) % maximum;

    const repelPoint = (x, y, radius, strength) => {
      if (!canHover || !pointerInJamverse) return { x, y, force: 0 };
      const dx = x - cursorClientX;
      const dy = y - cursorClientY;
      const distance = Math.hypot(dx, dy);
      if (distance >= radius) return { x, y, force: 0 };
      const safeDistance = Math.max(distance, 0.001);
      const force = Math.pow(1 - distance / radius, 1.7);
      return {
        x: x + (dx / safeDistance) * force * strength,
        y: y + (dy / safeDistance) * force * strength,
        force,
      };
    };

    const calmlyBendPoint = (x, y, radius, strength) => {
      if (!canHover || !pointerInJamverse) return { x, y, force: 0 };
      const calmCursorX = (pointerX * 0.5 + 0.5) * canvasWidth;
      const calmCursorY = (pointerY * 0.5 + 0.5) * canvasHeight;
      const dx = x - calmCursorX;
      const dy = y - calmCursorY;
      const distance = Math.hypot(dx, dy);
      if (distance >= radius) return { x, y, force: 0 };
      const safeDistance = Math.max(distance, 0.001);
      const force = Math.pow(1 - distance / radius, 2.25);
      return {
        x: x + (dx / safeDistance) * force * strength,
        y: y + (dy / safeDistance) * force * strength,
        force,
      };
    };

    const drawDropAtmosphere = (time) => {
      particles.forEach((particle, index) => {
        const drift = time * 0.012 * particle.speed;
        const x = wrap(particle.x * canvasWidth + drift * 22 + pointerX * (index % 3), canvasWidth);
        const y = wrap(particle.y * canvasHeight - drift * 9, canvasHeight);
        const point = repelPoint(x, y, 170, 118 + particle.size * 48);
        const size = 2.8 + particle.size * 7.2;
        canvasContext.save();
        canvasContext.translate(point.x, point.y);
        canvasContext.rotate(time * 0.00034 * particle.speed + particle.phase * Math.PI * 2 + point.force * 1.8);
        canvasContext.fillStyle = index % 6 === 0
          ? 'rgba(255,210,55,.78)'
          : index % 3 === 0
            ? 'rgba(255,126,188,.78)'
            : 'rgba(238,248,255,.68)';
        canvasContext.beginPath();
        canvasContext.ellipse(0, 0, size * 1.24, size * 0.52, 0, 0, Math.PI * 2);
        canvasContext.fill();
        canvasContext.restore();
        if (index % 9 === 0) {
          canvasContext.strokeStyle = 'rgba(72,180,255,.26)';
          canvasContext.lineWidth = 1;
          canvasContext.beginPath();
          canvasContext.moveTo(point.x - 16, point.y);
          canvasContext.lineTo(point.x + 16, point.y);
          canvasContext.stroke();
        }
      });
    };

    const drawRainAtmosphere = (time) => {
      const slant = 10 + clamp(scrollVelocity / 9, -12, 18);
      canvasContext.lineCap = 'round';
      particles.forEach((particle, index) => {
        const speed = 0.34 + particle.speed * 0.42;
        const y = wrap(particle.y * canvasHeight + time * speed, canvasHeight + 90) - 45;
        const x = wrap(particle.x * canvasWidth + time * speed * 0.12 + pointerX * 2.2, canvasWidth + 80) - 40;
        const length = 18 + particle.size * 36;
        const start = repelPoint(x, y, 178, 116);
        const end = repelPoint(x - slant, y + length, 178, 148);
        canvasContext.strokeStyle = index % 8 === 0 ? 'rgba(245,49,151,.52)' : 'rgba(130,224,255,.42)';
        canvasContext.lineWidth = 0.7 + particle.size * 1.1;
        canvasContext.beginPath();
        canvasContext.moveTo(start.x, start.y);
        canvasContext.lineTo(end.x, end.y);
        canvasContext.stroke();
        if (index % 13 === 0 && y > canvasHeight * 0.82) {
          canvasContext.strokeStyle = 'rgba(72,231,255,.24)';
          canvasContext.beginPath();
          canvasContext.ellipse(end.x, canvasHeight * 0.9, 10 + particle.size * 12, 2.5, 0, 0, Math.PI * 2);
          canvasContext.stroke();
        }
      });
    };

    const drawStickerAtmosphere = (time) => {
      particles.forEach((particle, index) => {
        const x = wrap(particle.x * canvasWidth + time * (0.018 + particle.speed * 0.028) + pointerX * (index % 4), canvasWidth + 90) - 45;
        const y = wrap(particle.y * canvasHeight + Math.sin(time * 0.001 + particle.phase * 8) * 28, canvasHeight);
        const point = repelPoint(x, y, 190, 148 + particle.size * 64);
        const size = 4 + particle.size * 9;
        const colors = ['rgba(245,49,151,.72)', 'rgba(255,210,55,.76)', 'rgba(7,26,74,.62)', 'rgba(72,231,255,.6)'];
        canvasContext.save();
        canvasContext.translate(point.x, point.y);
        canvasContext.rotate(time * 0.0003 * particle.speed + particle.phase * Math.PI + point.force * 2.4);
        canvasContext.fillStyle = colors[index % colors.length];
        if (particle.kind === 0) {
          canvasContext.fillRect(-size, -size * 0.28, size * 2, size * 0.56);
        } else if (particle.kind === 1) {
          canvasContext.beginPath();
          canvasContext.arc(0, 0, size * 0.62, 0, Math.PI * 2);
          canvasContext.fill();
        } else {
          canvasContext.beginPath();
          canvasContext.moveTo(0, -size);
          canvasContext.lineTo(size * 0.72, size * 0.72);
          canvasContext.lineTo(-size * 0.72, size * 0.72);
          canvasContext.closePath();
          canvasContext.fill();
        }
        canvasContext.restore();
      });
    };

    const drawSunAtmosphere = (time) => {
      particles.forEach((particle, index) => {
        const scrollDriftX = jamverseRawProgress * canvasWidth * (0.32 + particle.speed * 0.18);
        const scrollDriftY = jamverseRawProgress * canvasHeight * ((index % 2 === 0 ? -0.08 : 0.055) + particle.size * 0.025);
        const x = wrap(particle.x * canvasWidth + time * (0.009 + particle.speed * 0.01) + scrollDriftX, canvasWidth + 60) - 30;
        const y = wrap(particle.y * canvasHeight + Math.sin(time * 0.00072 + particle.phase * 12) * 36 + time * 0.0045 + scrollDriftY, canvasHeight + 50) - 25;
        const point = repelPoint(x, y, 188, 92 + particle.size * 38);
        const size = 3 + particle.size * 8;
        canvasContext.save();
        canvasContext.translate(point.x, point.y);
        canvasContext.rotate(Math.sin(time * 0.001 + particle.phase * 8) * 1.4 + point.force * 1.8);
        canvasContext.fillStyle = index % 5 === 0 ? 'rgba(10,77,150,.42)' : index % 3 === 0 ? 'rgba(245,146,201,.62)' : 'rgba(255,255,255,.68)';
        canvasContext.beginPath();
        canvasContext.ellipse(0, 0, size * 1.25, size * 0.48, 0, 0, Math.PI * 2);
        canvasContext.fill();
        canvasContext.restore();
      });
    };

    const drawNeonAtmosphere = (time) => {
      canvasContext.lineCap = 'round';
      canvasContext.globalCompositeOperation = 'lighter';
      particles.forEach((particle, index) => {
        const speed = 0.016 + particle.speed * 0.03;
        const x = wrap(particle.x * canvasWidth + time * speed, canvasWidth + 180) - 90;
        const y = particle.y * canvasHeight + Math.sin(time * 0.00032 + particle.phase * 8) * 6;
        const length = 14 + particle.size * 24;
        const start = calmlyBendPoint(x, y, 174, 16 + particle.size * 5);
        const end = calmlyBendPoint(x + length, y, 174, 21 + particle.size * 6);
        canvasContext.strokeStyle = index % 4 === 0 ? 'rgba(245,49,151,.3)' : index % 7 === 0 ? 'rgba(255,210,55,.26)' : 'rgba(72,231,255,.29)';
        canvasContext.lineWidth = 0.55 + particle.size * 0.9;
        canvasContext.beginPath();
        canvasContext.moveTo(start.x, start.y);
        canvasContext.lineTo(end.x, end.y);
        canvasContext.stroke();
      });
      canvasContext.globalCompositeOperation = 'source-over';
    };

    const drawHelmetAtmosphere = (time, worldIndex) => {
      const palettes = [
        ['rgba(255,210,55,.74)', 'rgba(255,255,255,.28)'],
        ['rgba(166,255,0,.7)', 'rgba(245,49,151,.64)'],
        ['rgba(166,255,0,.72)', 'rgba(255,74,52,.62)'],
        ['rgba(255,118,205,.72)', 'rgba(96,230,255,.62)'],
        ['rgba(99,92,255,.72)', 'rgba(72,231,255,.62)'],
      ];
      const palette = palettes[clamp(worldIndex - 5, 0, palettes.length - 1)];
      canvasContext.globalCompositeOperation = 'lighter';
      particles.forEach((particle, index) => {
        const angle = particle.phase * Math.PI * 2 + time * (0.00008 + particle.speed * 0.00006);
        const radius = Math.min(canvasWidth, canvasHeight) * (0.18 + particle.size * 0.48);
        const x = canvasWidth * 0.66 + Math.cos(angle) * radius + pointerX * 12;
        const y = canvasHeight * 0.5 + Math.sin(angle * 1.3) * radius * 0.56 + pointerY * 8;
        const point = repelPoint(x, y, 212, 152 + particle.size * 72);
        const size = 1.5 + particle.size * 5;
        canvasContext.save();
        canvasContext.translate(point.x, point.y);
        canvasContext.rotate(angle + scrollVelocity * 0.004 + point.force * 2.1);
        canvasContext.fillStyle = palette[index % 2];
        if ((index + worldIndex) % 3 === 0) {
          canvasContext.fillRect(-size * 3, -0.7, size * 6, 1.4);
        } else {
          canvasContext.beginPath();
          canvasContext.moveTo(0, -size);
          canvasContext.lineTo(size, 0);
          canvasContext.lineTo(0, size);
          canvasContext.lineTo(-size, 0);
          canvasContext.closePath();
          canvasContext.fill();
        }
        canvasContext.restore();
      });
      canvasContext.globalCompositeOperation = 'source-over';
    };

    const drawAtmosphere = (time) => {
      if (!canvasContext || canvasWidth < 1) return;
      canvasContext.clearRect(0, 0, canvasWidth, canvasHeight);
      canvasContext.save();
      if (activeWorld === 0) drawDropAtmosphere(time);
      if (activeWorld === 1) drawRainAtmosphere(time);
      if (activeWorld === 2) drawStickerAtmosphere(time);
      if (activeWorld === 3) drawSunAtmosphere(time);
      if (activeWorld === 4) drawNeonAtmosphere(time);
      if (activeWorld >= 5) drawHelmetAtmosphere(time, Number(nodes.worlds[activeWorld]?.dataset.sjWorld));
      canvasContext.restore();
    };

    const placeAtmosphereBehindForeground = () => {
      const world = nodes.worlds[activeWorld];
      if (!nodes.atmosphere || !world || nodes.atmosphere.parentElement === world) return;

      /*
       * The world backdrop is layer 1. The atmosphere canvas is layer 2.
       * Copy, product, price badge and colour controls start at layer 20.
       * Reparenting the single canvas into the active isolated world makes
       * that ordering real instead of allowing a sticky-level canvas to sit
       * over the complete interface.
       */
      const foreground = world.querySelector('.sj-world__copy');
      world.insertBefore(nodes.atmosphere, foreground || null);
    };

    const setJamverseState = (desktopMotion) => {
      if (!nodes.jamverse || nodes.worlds.length === 0) return;

      const phoneJourney = window.innerWidth < 750 && nodes.jamverseWorlds;
      let progress = mobileGestureProgress === null
        ? (phoneJourney && mobileSelectedProgress !== null
          ? mobileSelectedProgress
          : runwayProgress(nodes.jamverse))
        : mobileGestureProgress;
      if (!desktopMotion && !phoneJourney) {
        const sectionRect = nodes.jamverse.getBoundingClientRect();
        const sectionTravel = Math.max(nodes.jamverse.offsetHeight - window.innerHeight, 1);
        progress = clamp(-sectionRect.top / sectionTravel);
      }
      jamverseRawProgress = progress;

      const staged = desktopMotion
        ? stageJamverseProgress(progress, nodes.worlds.length)
        : { progress, scaled: progress * Math.max(nodes.worlds.length - 1, 1) };
      const sceneProgress = staged.progress;
      const scaled = staged.scaled;
      const lowerIndex = Math.min(Math.floor(scaled), nodes.worlds.length - 1);
      const upperIndex = Math.min(lowerIndex + 1, nodes.worlds.length - 1);
      const blend = scaled - lowerIndex;
      const previousWorld = activeWorld;
      activeWorld = Math.round(scaled);
      placeAtmosphereBehindForeground();

      root.dataset.sjWorld = String(activeWorld);
      root.style.setProperty('--sj-jv-progress', sceneProgress.toFixed(4));
      root.style.setProperty('--sj-jv-percent', `${(progress * 100).toFixed(2)}%`);
      root.style.setProperty('--sj-drop-portal-scale', (1 + easeOut(clamp(sceneProgress / 0.12)) * 1.18).toFixed(4));

      const paperTravel = segment(sceneProgress, 0.17, 0.31);
      const paperOpacity = Math.min(segment(sceneProgress, 0.17, 0.22), 1 - segment(sceneProgress, 0.27, 0.32));
      root.style.setProperty('--sj-paper-opacity', clamp(paperOpacity).toFixed(4));
      root.style.setProperty('--sj-paper-x', `${(110 - paperTravel * 230).toFixed(2)}%`);

      nodes.worlds.forEach((world, index) => {
        const phoneWorldActive = index === activeWorld;
        const center = index / Math.max(nodes.worlds.length - 1, 1);
        const local = clamp((sceneProgress - (center - 0.125)) / 0.25);
        let opacity = 0;
        if (index === lowerIndex) opacity = lowerIndex === upperIndex ? 1 : 1 - blend;
        if (index === upperIndex) opacity = lowerIndex === upperIndex ? 1 : blend;

        if (phoneJourney) {
          world.style.removeProperty('--sj-mobile-world-x');
          world.style.setProperty('visibility', phoneWorldActive ? 'visible' : 'hidden', 'important');
          world.setAttribute('aria-hidden', String(!phoneWorldActive));
          world.classList.toggle('is-active', phoneWorldActive);
          world.style.setProperty('--world-opacity', phoneWorldActive ? '1' : '0');
          if (!phoneWorldActive) return;
          world.style.setProperty('--world-copy-x', '0vw');
          world.style.setProperty('--world-copy-y', '0vh');
          world.style.setProperty('--world-product-x', '0vw');
          world.style.setProperty('--world-product-y', '0vh');
          world.style.setProperty('--world-product-scale', '1');
          world.style.setProperty('--world-local', '1');
          world.style.setProperty('--world-back-x', '0vw');
          world.style.setProperty('--world-near-x', '0vw');
          world.style.setProperty('--world-scale', '1');
          world.style.setProperty('--world-stage-scale', '1');
          return;
        } else {
          world.removeAttribute('aria-hidden');
          world.style.removeProperty('visibility');
        }

        const travel = 0.5 - local;
        world.style.setProperty('--world-opacity', opacity.toFixed(4));
        world.style.setProperty('--world-copy-x', `${(travel * 30).toFixed(2)}vw`);
        world.style.setProperty('--world-copy-y', `${(Math.abs(travel) * -3.5).toFixed(2)}vh`);
        world.style.setProperty('--world-product-x', `${(travel * -23).toFixed(2)}vw`);
        world.style.setProperty('--world-product-y', `${(Math.sin(local * Math.PI) * -1.4).toFixed(2)}vh`);
        world.style.setProperty('--world-product-scale', (0.91 + Math.sin(local * Math.PI) * 0.09).toFixed(4));
        world.style.setProperty('--world-local', local.toFixed(4));
        world.style.setProperty('--world-back-x', `${(travel * -12).toFixed(2)}vw`);
        world.style.setProperty('--world-near-x', `${(travel * -42).toFixed(2)}vw`);
        world.style.setProperty('--world-scale', (1.035 - opacity * 0.035).toFixed(4));
        world.style.setProperty('--world-stage-scale', (0.975 + opacity * 0.025).toFixed(4));
        if (!phoneJourney) {
          world.style.visibility = opacity < 0.002 && desktopMotion ? 'hidden' : 'visible';
        }
        world.classList.toggle('is-active', index === activeWorld);
      });

      nodes.jamverseButtons.forEach((button, index) => {
        if (index === activeWorld) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });

      if (phoneJourney && previousWorld !== activeWorld) {
        updateMobileWorldMedia(activeWorld);
      }

      if (phoneJourney && previousWorld !== activeWorld && nodes.jamverseChapters) {
        const activeButton = nodes.jamverseButtons[activeWorld];
        if (activeButton) {
          const left = activeButton.offsetLeft - Math.max((nodes.jamverseChapters.clientWidth - activeButton.offsetWidth) * 0.5, 0);
          nodes.jamverseChapters.scrollTo({ left, behavior: 'smooth' });
        }
      }

      if (nodes.worldNumber) nodes.worldNumber.textContent = String(activeWorld + 1).padStart(2, '0');
      if (nodes.worldName) nodes.worldName.textContent = WORLD_NAMES[Number(nodes.worlds[activeWorld]?.dataset.sjWorld)] || WORLD_NAMES[0];
      if (nodes.worldTransport) nodes.worldTransport.textContent = WORLD_TRANSPORT[Number(nodes.worlds[activeWorld]?.dataset.sjWorld)] || WORLD_TRANSPORT[0];
      setPercent(nodes.jamversePercent, progress);
    };

    const setGarageState = (desktopMotion) => {
      if (!nodes.garage || nodes.railCards.length === 0) return;
      const previousGarage = activeGarage;
      let progress = 0;
      if (desktopMotion) {
        progress = runwayProgress(nodes.garage);
      } else if (nodes.railTrack) {
        progress = clamp(nodes.railTrack.scrollLeft / Math.max(nodes.railTrack.scrollWidth - nodes.railTrack.clientWidth, 1));
      }

      const activeFloat = progress * Math.max(nodes.railCards.length - 1, 1);
      activeGarage = Math.round(activeFloat);
      if (activeGarage !== previousGarage) updateMobileGarageMedia(activeGarage, true);
      nodes.garage.dataset.sjGarageActive = String(activeGarage);
      root.style.setProperty('--sj-rail-progress', progress.toFixed(4));
      root.style.setProperty('--sj-rail-percent', `${(progress * 100).toFixed(2)}%`);
      setPercent(nodes.railPercent, progress, '%');

      nodes.railCards.forEach((card, index) => {
        const distance = index - activeFloat;
        const absoluteDistance = Math.abs(distance);
        const x = distance * Math.min(window.innerWidth * 0.31, 450);
        const scale = clamp(1 - absoluteDistance * 0.18, 0.62, 1);
        const opacity = clamp(1 - absoluteDistance * 0.39, 0.16, 1);
        card.style.setProperty('--sj-garage-card-x', `${x.toFixed(2)}px`);
        card.style.setProperty('--sj-garage-card-scale', scale.toFixed(4));
        card.style.setProperty('--sj-garage-card-opacity', opacity.toFixed(4));
        card.style.setProperty('--sj-garage-card-rotate', `${clamp(distance * 3.4, -8, 8).toFixed(2)}deg`);
        card.style.setProperty('--sj-card-progress', clamp(1 - absoluteDistance).toFixed(4));
        card.classList.toggle('is-active', index === activeGarage);
      });

      nodes.garageMarkers.forEach((button, index) => {
        if (index === activeGarage) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });

      const activeTitle = nodes.railCards[activeGarage]?.querySelector('h3')?.textContent?.trim();
      if (nodes.garageName && activeTitle) nodes.garageName.textContent = activeTitle.toUpperCase();
    };

    const preloadHelmetHero = (index) => {
      const card = nodes.helmetCards[index];
      const source = card?.dataset.sjHelmetHero;
      if (!source || loadedHelmetHeroes.has(source)) return;
      const image = new Image();
      image.decoding = 'async';
      image.src = source;
      loadedHelmetHeroes.add(source);
    };

    const hydrateHelmetThumbs = (centerIndex = activeHelmet) => {
      if (window.innerWidth >= 990) {
        hydrateHelmetThumbImages(nodes.helmetRiot);
        return;
      }
      hydrateHelmetThumbImages(nodes.helmetRiot, centerIndex, 2);
    };

    const setActiveHelmet = (nextIndex, announce = false) => {
      if (!nodes.helmetRiot || nodes.helmetCards.length === 0) return;
      const index = Math.round(clamp(nextIndex, 0, nodes.helmetCards.length - 1));
      const card = nodes.helmetCards[index];
      if (!card) return;

      activeHelmet = index;
      nodes.helmetRiot.dataset.sjHelmetActive = String(index);
      nodes.helmetRiot.dataset.sjHelmetAccent = card.dataset.sjHelmetAccent || 'cyan';
      if (helmetRiotVisible) hydrateHelmetThumbs(index);

      const title = card.dataset.sjHelmetTitle || 'StreetJam Helmet';
      const price = card.dataset.sjHelmetPrice || '';
      const status = card.dataset.sjHelmetStatus || '';
      const url = card.dataset.sjHelmetUrl || '#';
      const mood = card.dataset.sjHelmetMood || 'Helmet Riot';
      const heroSource = card.dataset.sjHelmetHero;

      if (
        nodes.helmetHero
        && heroSource
        && helmetRiotVisible
        && window.innerWidth >= 990
        && nodes.helmetHero.getAttribute('src') !== heroSource
      ) {
        nodes.helmetHero.src = heroSource;
        nodes.helmetHero.alt = title;
      }
      nodes.helmetLinks.forEach((link) => {
        link.href = url;
        link.setAttribute('aria-label', `View ${title}`);
      });
      if (nodes.helmetTitle) nodes.helmetTitle.textContent = title;
      if (nodes.helmetPrice) nodes.helmetPrice.textContent = price;
      if (nodes.helmetStatus) {
        nodes.helmetStatus.textContent = status;
        nodes.helmetStatus.classList.toggle('is-sold-out', status.includes('OUT OF STOCK'));
      }
      if (nodes.helmetIndex) nodes.helmetIndex.textContent = String(index + 1).padStart(2, '0');
      if (nodes.helmetMood) nodes.helmetMood.textContent = mood.toUpperCase();
      if (nodes.helmetMoodCopy) nodes.helmetMoodCopy.textContent = mood.toUpperCase();

      nodes.helmetCards.forEach((item, cardIndex) => {
        item.classList.toggle('is-active', cardIndex === index);
        const link = item.querySelector('a');
        if (link) {
          if (cardIndex === index) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        }
      });
      nodes.helmetMarkers.forEach((marker, markerIndex) => {
        if (markerIndex === index) marker.setAttribute('aria-current', 'true');
        else marker.removeAttribute('aria-current');
      });

      const activeMarker = nodes.helmetMarkers[index];
      const markerRail = activeMarker?.parentElement;
      if (window.innerWidth < 990 && activeMarker && markerRail) {
        const markerLeft = activeMarker.offsetLeft
          - Math.max((markerRail.clientWidth - activeMarker.clientWidth) * 0.5, 0);
        markerRail.scrollTo({ left: Math.max(markerLeft, 0), behavior: announce ? 'smooth' : 'auto' });
      }

      if (helmetRiotVisible && window.innerWidth >= 990) {
        preloadHelmetHero(index);
        preloadHelmetHero(Math.max(index - 1, 0));
        preloadHelmetHero(Math.min(index + 1, nodes.helmetCards.length - 1));
      }

      if (announce && nodes.helmetLive) {
        nodes.helmetLive.textContent = `${title}, ${price}, ${status}`;
      }
    };

    const setHelmetRiotState = (desktopMotion) => {
      if (!nodes.helmetRiot || nodes.helmetCards.length === 0) return;

      let progress = 0;
      if (desktopMotion) {
        progress = runwayProgress(nodes.helmetRiot);
      } else if (nodes.helmetCarousel) {
        progress = clamp(
          nodes.helmetCarousel.scrollLeft
          / Math.max(nodes.helmetCarousel.scrollWidth - nodes.helmetCarousel.clientWidth, 1),
        );
      }

      const activeFloat = progress * Math.max(nodes.helmetCards.length - 1, 1);
      const rounded = Math.round(activeFloat);
      root.style.setProperty('--sj-riot-progress', progress.toFixed(4));
      root.style.setProperty('--sj-riot-front-x', `${(-activeFloat * 194).toFixed(2)}px`);
      root.style.setProperty('--sj-riot-rear-x', `${(activeFloat * 112).toFixed(2)}px`);
      root.style.setProperty('--sj-riot-hero-rotate', `${clamp((rounded - activeFloat) * 10 + scrollVelocity * 0.035, -7, 7).toFixed(2)}deg`);
      setPercent(nodes.helmetPercent, progress, '%');

      nodes.helmetCards.forEach((card, index) => {
        const distance = index - activeFloat;
        const absoluteDistance = Math.abs(distance);
        card.style.setProperty('--sj-riot-card-distance', distance.toFixed(4));
        card.style.setProperty('--sj-riot-card-scale', clamp(1 - absoluteDistance * 0.12, 0.72, 1).toFixed(4));
        card.style.setProperty('--sj-riot-card-opacity', clamp(1 - absoluteDistance * 0.22, 0.22, 1).toFixed(4));
        card.style.setProperty('--sj-riot-card-rotate', `${clamp(distance * 2.4, -8, 8).toFixed(2)}deg`);
      });

      if (rounded !== activeHelmet) setActiveHelmet(rounded);
    };

    const updateScrollEffects = () => {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY;
      lastScrollY = currentScrollY;
      scrollVelocity += (delta - scrollVelocity) * 0.3;
      const velocity = clamp(scrollVelocity / 72, -1, 1);
      const desktopMotion = window.innerWidth >= 990;
      const jamverseDesktop = window.innerWidth >= 750;

      root.style.setProperty('--sj-scroll-velocity', velocity.toFixed(4));
      const pointerProperties = {
        '--sj-pointer-x': `${(pointerX * 22).toFixed(2)}px`,
        '--sj-pointer-y': `${(pointerY * 16).toFixed(2)}px`,
        '--sj-pointer-soft-x': `${(pointerX * 12).toFixed(2)}px`,
        '--sj-pointer-soft-y': `${(pointerY * 9).toFixed(2)}px`,
        '--sj-pointer-far-x': `${(pointerX * -8).toFixed(2)}px`,
        '--sj-pointer-far-y': `${(pointerY * -6).toFixed(2)}px`,
        '--sj-pointer-fast-x': `${(pointerX * 22).toFixed(2)}px`,
        '--sj-pointer-fast-y': `${(pointerY * 16).toFixed(2)}px`,
        '--sj-mascot-rey-x': `${mascotReyX.toFixed(2)}px`,
        '--sj-mascot-rey-y': `${mascotReyY.toFixed(2)}px`,
        '--sj-mascot-rey-r': `${clamp(mascotReyX * 0.07, -1.6, 1.6).toFixed(2)}deg`,
        '--sj-mascot-mira-x': `${mascotMiraX.toFixed(2)}px`,
        '--sj-mascot-mira-y': `${mascotMiraY.toFixed(2)}px`,
        '--sj-mascot-mira-r': `${clamp(mascotMiraX * -0.09, -2.2, 2.2).toFixed(2)}deg`,
        '--sj-mascot-zipp-x': `${mascotZippX.toFixed(2)}px`,
        '--sj-mascot-zipp-y': `${mascotZippY.toFixed(2)}px`,
        '--sj-mascot-zipp-r': `${clamp((mascotZippX + mascotZippY) * 0.12, -3.4, 3.4).toFixed(2)}deg`,
        '--sj-cursor-x': `${cursorClientX.toFixed(2)}px`,
        '--sj-cursor-y': `${cursorClientY.toFixed(2)}px`,
        '--sj-cursor-scale': canHover ? '1' : '.68',
      };
      Object.entries(pointerProperties).forEach(([property, value]) => {
        root.style.setProperty(property, value);
        nodes.jamverse?.style.setProperty(property, value);
      });

      if (jamverseVisible) setJamverseState(jamverseDesktop);

      const brandProgress = viewportProgress(nodes.brandStrike);
      root.style.setProperty('--sj-brand-progress', brandProgress.toFixed(4));

      if (garageVisible) setGarageState(desktopMotion);
      if (helmetRiotVisible) setHelmetRiotState(desktopMotion);

      if (nodes.clarity) {
        const clarityProgress = viewportProgress(nodes.clarity);
        root.style.setProperty('--sj-clarity-progress', clarityProgress.toFixed(4));
        nodes.clarityRows.forEach((row, index) => {
          const rowProgress = smooth(clamp((clarityProgress - 0.12 - index * 0.08) / 0.28));
          row.style.setProperty('--sj-row-progress', rowProgress.toFixed(4));
        });
      }

      if (nodes.finale) {
        const finaleProgress = viewportProgress(nodes.finale);
        root.style.setProperty('--sj-finale-progress', finaleProgress.toFixed(4));
      }
    };

    const frame = (time) => {
      rafId = 0;
      const phoneLayout = window.innerWidth < 750;
      const deltaTime = Math.min(time - previousFrameTime, 48);
      previousFrameTime = time;
      const pointerEase = 1 - Math.pow(0.0004, deltaTime / 1000);
      pointerX += (pointerTargetX - pointerX) * pointerEase;
      pointerY += (pointerTargetY - pointerY) * pointerEase;

      const reyTargetX = pointerTargetX * 18 + pointerTargetY * 4;
      const reyTargetY = pointerTargetY * 11 - pointerTargetX * 2;
      const miraTargetX = pointerTargetX * -22 + pointerTargetY * 3;
      const miraTargetY = pointerTargetY * 15 + pointerTargetX * 5;
      const zippTargetX = pointerTargetY * 16 + pointerTargetX * 7;
      const zippTargetY = pointerTargetX * -12 + pointerTargetY * 5;
      const reyEase = 1 - Math.pow(0.0018, deltaTime / 1000);
      const miraEase = 1 - Math.pow(0.012, deltaTime / 1000);
      const zippEase = 1 - Math.pow(0.00008, deltaTime / 1000);
      mascotReyX += (reyTargetX - mascotReyX) * reyEase;
      mascotReyY += (reyTargetY - mascotReyY) * reyEase;
      mascotMiraX += (miraTargetX - mascotMiraX) * miraEase;
      mascotMiraY += (miraTargetY - mascotMiraY) * miraEase;
      mascotZippX += (zippTargetX - mascotZippX) * zippEase;
      mascotZippY += (zippTargetY - mascotZippY) * zippEase;
      scrollVelocity *= phoneLayout ? 0 : needsUpdate ? 0.92 : 0.78;

      const pointerMoving = Math.abs(pointerTargetX - pointerX) > 0.002 || Math.abs(pointerTargetY - pointerY) > 0.002;
      const mascotMoving = nodes.mascots.length > 0 && (
        Math.abs(reyTargetX - mascotReyX) > 0.03 || Math.abs(reyTargetY - mascotReyY) > 0.03 ||
        Math.abs(miraTargetX - mascotMiraX) > 0.03 || Math.abs(miraTargetY - mascotMiraY) > 0.03 ||
        Math.abs(zippTargetX - mascotZippX) > 0.03 || Math.abs(zippTargetY - mascotZippY) > 0.03
      );

      const layoutNeedsUpdate = needsUpdate
        || (!phoneLayout && (pointerMoving || mascotMoving || Math.abs(scrollVelocity) > 0.08));
      if (layoutNeedsUpdate) updateScrollEffects();

      const atmosphereRunning = isFullMotion && jamverseVisible && document.visibilityState === 'visible';
      const atmosphereInterval = phoneLayout ? 1000 / 12 : 0;
      if (atmosphereRunning && (!phoneLayout || needsUpdate) && time - lastAtmosphereDraw >= atmosphereInterval) {
        drawAtmosphere(time);
        lastAtmosphereDraw = time;
      }
      needsUpdate = false;

      if (
        (!phoneLayout && atmosphereRunning)
        || pointerMoving
        || mascotMoving
        || Math.abs(scrollVelocity) > 0.08
        || needsUpdate
      ) {
        rafId = window.requestAnimationFrame(frame);
      }
    };

    const requestUpdate = () => {
      needsUpdate = true;
      if (!rafId) rafId = window.requestAnimationFrame(frame);
    };

    const jamverseScrollGeometry = () => {
      if (!nodes.jamverse) return { top: window.scrollY, travel: 1 };
      const sectionTop = window.scrollY + nodes.jamverse.getBoundingClientRect().top;
      const travel = Math.max(nodes.jamverse.offsetHeight - window.innerHeight, 1);
      return { top: sectionTop, travel };
    };

    const scrollJamverseTo = (index, behavior = 'smooth') => {
      if (!nodes.jamverse || nodes.worlds.length === 0) return;
      const targetIndex = Math.round(clamp(index, 0, nodes.worlds.length - 1));
      const progress = targetIndex / Math.max(nodes.worlds.length - 1, 1);
      if (window.innerWidth < 750) {
        mobileSelectedProgress = progress;
        mobileGestureProgress = null;
        requestUpdate();
        return;
      }
      const { top, travel } = jamverseScrollGeometry();
      window.scrollTo({ top: top + travel * clamp(progress), behavior });
    };

    const scrollGarageTo = (index) => {
      const targetIndex = clamp(index, 0, Math.max(nodes.railCards.length - 1, 0));
      if (!nodes.garage) return;
      if (window.innerWidth >= 990) {
        const sectionTop = window.scrollY + nodes.garage.getBoundingClientRect().top;
        const travel = Math.max(nodes.garage.offsetHeight - window.innerHeight, 1);
        const progress = targetIndex / Math.max(nodes.railCards.length - 1, 1);
        window.scrollTo({ top: sectionTop + travel * progress, behavior: 'smooth' });
      } else {
        const card = nodes.railCards[targetIndex];
        if (card && nodes.railTrack) {
          const left = card.offsetLeft - Math.max((nodes.railTrack.clientWidth - card.clientWidth) * 0.5, 0);
          nodes.railTrack.scrollTo({ left, behavior: 'smooth' });
        }
      }
      requestUpdate();
    };

    const scrollHelmetTo = (index, announce = true) => {
      if (!nodes.helmetRiot || nodes.helmetCards.length === 0) return;
      const targetIndex = Math.round(clamp(index, 0, nodes.helmetCards.length - 1));
      if (window.innerWidth >= 990) {
        const sectionTop = window.scrollY + nodes.helmetRiot.getBoundingClientRect().top;
        const travel = Math.max(nodes.helmetRiot.offsetHeight - window.innerHeight, 1);
        const progress = targetIndex / Math.max(nodes.helmetCards.length - 1, 1);
        window.scrollTo({ top: sectionTop + travel * progress, behavior: 'smooth' });
      } else {
        const card = nodes.helmetCards[targetIndex];
        if (card && nodes.helmetCarousel) {
          const left = card.offsetLeft - Math.max((nodes.helmetCarousel.clientWidth - card.clientWidth) * 0.5, 0);
          nodes.helmetCarousel.scrollTo({ left, behavior: 'smooth' });
        }
      }
      setActiveHelmet(targetIndex, announce);
      requestUpdate();
    };

    nodes.jamverseButtons.forEach((button, index) => {
      button.addEventListener('click', () => scrollJamverseTo(index));
    });
    nodes.jamversePrev?.addEventListener('click', () => scrollJamverseTo(activeWorld - 1));
    nodes.jamverseNext?.addEventListener('click', () => scrollJamverseTo(activeWorld + 1));

    if (nodes.jamverseWorlds) {
      nodes.jamverseWorlds.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        scrollJamverseTo(activeWorld + (event.key === 'ArrowRight' ? 1 : -1));
      });

      nodes.jamverseWorlds.addEventListener('pointerdown', (event) => {
        if (window.innerWidth >= 750) return;
        if (event.target.closest('button, input, select, textarea, [role="button"]')) return;
        mobileSwipe = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          lastX: event.clientX,
          lastTime: event.timeStamp,
          velocityX: 0,
          startProgress: mobileSelectedProgress === null
            ? jamverseRawProgress
            : mobileSelectedProgress,
          axis: null,
        };
        if (nodes.jamverseWorlds.setPointerCapture) {
          nodes.jamverseWorlds.setPointerCapture(event.pointerId);
        }
      }, { passive: true });

      nodes.jamverseWorlds.addEventListener('pointermove', (event) => {
        if (!mobileSwipe || mobileSwipe.pointerId !== event.pointerId || window.innerWidth >= 750) return;
        const deltaX = event.clientX - mobileSwipe.startX;
        const deltaY = event.clientY - mobileSwipe.startY;

        if (!mobileSwipe.axis && Math.hypot(deltaX, deltaY) >= 10) {
          mobileSwipe.axis = Math.abs(deltaX) > Math.abs(deltaY) * 1.08 ? 'horizontal' : 'vertical';
        }
        if (mobileSwipe.axis !== 'horizontal') return;

        if (event.cancelable) event.preventDefault();
        const elapsed = Math.max(event.timeStamp - mobileSwipe.lastTime, 1);
        mobileSwipe.velocityX = (event.clientX - mobileSwipe.lastX) / elapsed;
        mobileSwipe.lastX = event.clientX;
        mobileSwipe.lastTime = event.timeStamp;

        const worldDelta = -deltaX / Math.max(nodes.jamverseWorlds.clientWidth, 1);
        mobileGestureProgress = clamp(
          mobileSwipe.startProgress
          + worldDelta / Math.max(nodes.worlds.length - 1, 1),
        );
        requestUpdate();
      }, { passive: false });

      const finishMobileSwipe = (event) => {
        if (!mobileSwipe || mobileSwipe.pointerId !== event.pointerId) return;
        const swipe = mobileSwipe;
        mobileSwipe = null;

        if (swipe.axis !== 'horizontal' || mobileGestureProgress === null) {
          mobileGestureProgress = null;
          requestUpdate();
          return;
        }

        const deltaX = event.clientX - swipe.startX;
        const startWorld = swipe.startProgress * Math.max(nodes.worlds.length - 1, 1);
        const currentWorld = mobileGestureProgress * Math.max(nodes.worlds.length - 1, 1);
        let targetWorld = Math.round(currentWorld);
        const decisiveFlick = Math.abs(deltaX) >= 42 || Math.abs(swipe.velocityX) >= 0.35;
        if (decisiveFlick && targetWorld === Math.round(startWorld)) {
          targetWorld += deltaX < 0 ? 1 : -1;
        }

        targetWorld = Math.round(clamp(targetWorld, 0, nodes.worlds.length - 1));
        suppressWorldClickUntil = performance.now() + 420;
        mobileGestureProgress = null;
        scrollJamverseTo(targetWorld);
        requestUpdate();
      };

      nodes.jamverseWorlds.addEventListener('pointerup', finishMobileSwipe, { passive: true });
      nodes.jamverseWorlds.addEventListener('pointercancel', finishMobileSwipe, { passive: true });
      nodes.jamverseWorlds.addEventListener('click', (event) => {
        if (performance.now() >= suppressWorldClickUntil) return;
        event.preventDefault();
        event.stopPropagation();
      }, true);

      nodes.jamverseWorlds.addEventListener('wheel', (event) => {
        if (window.innerWidth >= 750) return;
        if (Math.abs(event.deltaY) >= Math.abs(event.deltaX) || event.deltaX === 0) return;
        const currentProgress = mobileSelectedProgress === null
          ? jamverseRawProgress
          : mobileSelectedProgress;
        const boundedDeltaX = clamp(
          event.deltaX,
          -nodes.jamverseWorlds.clientWidth,
          nodes.jamverseWorlds.clientWidth,
        );
        const progressDelta = boundedDeltaX
          / Math.max(nodes.jamverseWorlds.clientWidth * Math.max(nodes.worlds.length - 1, 1), 1);
        const targetProgress = clamp(currentProgress + progressDelta);
        if (targetProgress === currentProgress) return;
        event.preventDefault();
        mobileSelectedProgress = targetProgress;
        requestUpdate();
      }, { passive: false });
    }

    nodes.garageMarkers.forEach((button) => {
      button.addEventListener('click', () => scrollGarageTo(Number(button.dataset.sjGarageJump)));
    });

    nodes.garagePrev?.addEventListener('click', () => scrollGarageTo(activeGarage - 1));
    nodes.garageNext?.addEventListener('click', () => scrollGarageTo(activeGarage + 1));
    nodes.garage?.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      scrollGarageTo(activeGarage + (event.key === 'ArrowRight' ? 1 : -1));
    });

    nodes.helmetMarkers.forEach((button) => {
      button.addEventListener('click', () => scrollHelmetTo(Number(button.dataset.sjHelmetJump)));
    });
    nodes.helmetPrev?.addEventListener('click', () => scrollHelmetTo(activeHelmet - 1));
    nodes.helmetNext?.addEventListener('click', () => scrollHelmetTo(activeHelmet + 1));
    nodes.helmetRiot?.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      scrollHelmetTo(activeHelmet + (event.key === 'ArrowRight' ? 1 : -1));
    });

    if (nodes.pointerStage && canHover && isFullMotion) {
      nodes.pointerStage.addEventListener('pointermove', (event) => {
        const rect = nodes.pointerStage.getBoundingClientRect();
        pointerInJamverse = true;
        pointerTargetX = clamp((event.clientX - rect.left) / Math.max(rect.width, 1), 0, 1) * 2 - 1;
        pointerTargetY = clamp((event.clientY - rect.top) / Math.max(rect.height, 1), 0, 1) * 2 - 1;
        cursorClientX = event.clientX;
        cursorClientY = event.clientY;
        if (nodes.cursorLight) nodes.cursorLight.style.opacity = '.74';
        requestUpdate();
      }, { passive: true });
      nodes.pointerStage.addEventListener('pointerleave', () => {
        pointerInJamverse = false;
        pointerTargetX = 0;
        pointerTargetY = 0;
        if (nodes.cursorLight) nodes.cursorLight.style.opacity = '0';
        requestUpdate();
      }, { passive: true });
    } else if (nodes.cursorLight) {
      nodes.cursorLight.style.display = 'none';
    }

    if (nodes.helmetPointer && nodes.helmetHeroWrap && canHover && isFullMotion) {
      nodes.helmetPointer.addEventListener('pointermove', (event) => {
        const rect = nodes.helmetPointer.getBoundingClientRect();
        const x = clamp((event.clientX - rect.left) / Math.max(rect.width, 1), 0, 1) * 2 - 1;
        const y = clamp((event.clientY - rect.top) / Math.max(rect.height, 1), 0, 1) * 2 - 1;
        root.style.setProperty('--sj-riot-pointer-x', `${(x * 4).toFixed(2)}deg`);
        root.style.setProperty('--sj-riot-pointer-y', `${(y * -4).toFixed(2)}deg`);
        requestUpdate();
      }, { passive: true });
      nodes.helmetPointer.addEventListener('pointerleave', () => {
        root.style.setProperty('--sj-riot-pointer-x', '0deg');
        root.style.setProperty('--sj-riot-pointer-y', '0deg');
        requestUpdate();
      }, { passive: true });
    }

    if ('IntersectionObserver' in window && nodes.jamverse) {
      const jamverseObserver = new IntersectionObserver((entries) => {
        jamverseVisible = entries.some((entry) => entry.isIntersecting);
        root.dataset.sjJamverseVisible = String(jamverseVisible);
        if (nodes.atmosphere) nodes.atmosphere.style.opacity = jamverseVisible ? '1' : '0';
        if (!jamverseVisible && canvasContext) canvasContext.clearRect(0, 0, canvasWidth, canvasHeight);
        requestUpdate();
      }, { rootMargin: '0px', threshold: 0 });
      jamverseObserver.observe(nodes.jamverse);
    }

    if ('IntersectionObserver' in window && nodes.garage) {
      const garageObserver = new IntersectionObserver((entries) => {
        garageVisible = entries.some((entry) => entry.isIntersecting);
        updateMobileGarageMedia(activeGarage, garageVisible);
        requestUpdate();
      }, { rootMargin: '20% 0px', threshold: 0 });
      garageObserver.observe(nodes.garage);
    } else if (nodes.garage) {
      garageVisible = true;
    }

    if ('IntersectionObserver' in window && nodes.helmetRiot) {
      const helmetObserver = new IntersectionObserver((entries) => {
        const wasVisible = helmetRiotVisible;
        helmetRiotVisible = entries.some((entry) => entry.isIntersecting);
        if (helmetRiotVisible && !wasVisible) {
          hydrateHelmetThumbs();
          setActiveHelmet(activeHelmet);
        }
        requestUpdate();
      }, { rootMargin: window.innerWidth < 750 ? '8% 0px' : '40% 0px', threshold: 0 });
      helmetObserver.observe(nodes.helmetRiot);
    } else if (nodes.helmetRiot) {
      helmetRiotVisible = true;
      hydrateHelmetThumbs();
    }

    root.classList.add('sj-motion-ready');
    resizeCanvas();
    setActiveHelmet(0);
    requestUpdate();

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', () => {
      syncMobileDocumentState();
      mobileGestureProgress = null;
      mobileSelectedProgress = window.innerWidth < 750
        ? activeWorld / Math.max(nodes.worlds.length - 1, 1)
        : null;
      mobileSwipe = null;
      if (window.innerWidth >= 990) buildHelmetRear();
      updateMobileWorldMedia(activeWorld);
      updateMobileGarageMedia(activeGarage, garageVisible);
      if (helmetRiotVisible) hydrateHelmetThumbs(activeHelmet);
      resizeCanvas();
      requestUpdate();
    }, { passive: true });
    nodes.railTrack?.addEventListener('scroll', requestUpdate, { passive: true });
    nodes.helmetCarousel?.addEventListener('scroll', requestUpdate, { passive: true });
    document.addEventListener('visibilitychange', requestUpdate, { passive: true });
  };

  const initialiseAll = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initialise);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll(), { once: true });
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
